import React from 'react';
import { Badge } from 'react-bootstrap';

/**
 * PartyBadge — renders a small colored badge for a normalized party label.
 *
 * Party comes from the backend's `normalize_party_label()` helper, so the
 * input is one of: 'DEM' | 'REP' | 'LIB' | 'GRE' | 'NPA' | 'IND' | 'WRI' | 'OTHER'.
 *
 * Note on colors: we don't use FL's official colors directly because
 * the bare blue/red would clash with our Bootstrap "primary"/"danger"
 * semantic intent across the app. Bootstrap variants give us
 * accessible contrast + light/dark mode handling for free, and the
 * association (blue = DEM, red = REP) still reads as intuitive at a
 * glance.
 */
const PARTY_VARIANT = {
  DEM: 'primary',
  REP: 'danger',
  LIB: 'warning',
  GRE: 'success',
  NPA: 'secondary',
  IND: 'secondary',
  WRI: 'dark',
  OTHER: 'light',
};

const PARTY_FULL = {
  DEM: 'Democratic',
  REP: 'Republican',
  LIB: 'Libertarian',
  GRE: 'Green',
  NPA: 'No Party Affiliation',
  IND: 'Independent',
  WRI: 'Write-in',
  OTHER: 'Other',
};

export function PartyBadge({ party, full = false, className = '', size = 'sm' }) {
  if (!party) return null;
  const label = String(party).toUpperCase();
  const variant = PARTY_VARIANT[label] || 'light';
  const text = full ? (PARTY_FULL[label] || label) : label;
  // 'light' variant needs dark text for contrast
  const textColor = variant === 'light' ? 'dark' : undefined;
  return (
    <Badge
      bg={variant}
      text={textColor}
      className={`${className} ${size === 'sm' ? 'small' : ''}`.trim()}
      data-testid={`party-badge-${label}`}
      title={PARTY_FULL[label] || label}
    >
      {text}
    </Badge>
  );
}

/**
 * PartyBadgeList — renders multiple party badges in a row.
 * Used on candidate list/detail when a candidate has more than one party
 * affiliation across races (rare but possible).
 */
export function PartyBadgeList({ parties = [], full = false, emptyText = '—', className = '' }) {
  if (!parties || parties.length === 0) {
    return <span className="text-muted small" data-testid="party-badge-empty">{emptyText}</span>;
  }
  return (
    <span className={`d-inline-flex gap-1 flex-wrap ${className}`.trim()}>
      {parties.map((p) => (
        <PartyBadge key={p} party={p} full={full} />
      ))}
    </span>
  );
}

export default PartyBadge;
